import { useState, useEffect } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Stack, Tooltip } from "@mui/material";
import Header from "../../components/Header";
import DebFormModal from "../../components/DebFormModal";
import {DebFormPasswordInput, DebFormSelect, DebFormTextInput} from "../../components/DebFormComponents";
import DataTable from "../../components/DataTable";
import { user } from "../../services/users";
import AddBoxIcon from '@mui/icons-material/AddBox';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import { editUserValidationSchema, newUserValidationSchema } from "../../schemas/user";
import copy from 'copy-to-clipboard';
import { ContentCopy } from "@mui/icons-material";

//TRANSICIÓN DEL ALERT DE ELIMINACIÓN
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const newUserValues = {
  nombre: "",
  email: "",
  password: "",
  doublePassword: "",
  role: ""
};

const roles = [
    { role: "ROLE_APPOINTMENTS", label: "Citas", value: 0 },
    { role: "ROLE_SURVEY", label: "Encuestas", value: 1 },
    { role: "ROLE_MOBILE", label: "Mobile", value: 2 },
    { role: "ROLE_SURVEY_API_EXTERNAL", label: "Encuestas API Externa", value: 3 }
];


function Users() {
  // MODAL DE EDICIÓN / CREACIÓN
  const [modalState, setModalState] = useState(false);
  const [modalInitialValues, setModalInitialValues] =
      useState(newUserValues);
  const [users, setUsers] = useState([]);

  const getUsers = async () => {
    setUsers(await user.getAll());
  };

  useEffect(() => {
    getUsers();
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values) => {
    try {
      if (values.id) {
        //estamos editando
        const res = await user.update(values);
        enqueueSnackbar('Usuario editado', {
          preventDuplicate: true,
          variant: 'success'
        });
      } else {
        const res = await user.create(values);
        enqueueSnackbar('Usuario creado', {
          preventDuplicate: true,
          variant: 'success'
        });
      }
      closeModal();
      getUsers();
    } catch (error) {
      if(values.id) {
        enqueueSnackbar("Ocurrió un error al editar el usuario", {
          preventDuplicate: true,
          variant: 'error'
        });
      } else if (error.response.status === 409) {
          enqueueSnackbar("Ya existe un usuario con el email y el sistema seleccionados", {
              preventDuplicate: true,
              variant: 'error'
          });
      } else {
        enqueueSnackbar("Ocurrió un error al crear el usuario", {
          preventDuplicate: true,
          variant: 'error'
        });
      }
    }
  };

  const openModal = () => {
    setModalState(true);
  };

  const closeModal = () => {
    setModalState(false);
    setModalInitialValues(newUserValues);
  };

  const handleEdit = (user) => {
    // Ponemos el password en blanco
    user.password = "";
    setModalInitialValues(user);
    openModal();
  };
  const handleCreateUser = () => {
    setModalInitialValues(newUserValues);
    openModal();
  };

  // ALERT DE ELIMINACIÓN
  const [selectedDelete, setSelectedDelete] = useState();
  const [openAlert, setOpenAlert] = useState(false);
  const handleAlertClose = () => {
    setOpenAlert(false);
  };
  const handleAlertOpen = (selectedUser) => {
    setSelectedDelete(selectedUser);
    setOpenAlert(true);
  };

  const handleDelete = async () => {
    try {
      const res = await user.delete(selectedDelete.id);
      enqueueSnackbar(("Se eliminó el usuario " + selectedDelete.nombre), {
        preventDuplicate: true,
        variant: 'success'
      });
      handleAlertClose();
      getUsers();
    } catch (error) {
      enqueueSnackbar("Ocurrió un error eliminando el usuario", {
        preventDuplicate: true,
        variant: 'error'
      } );
    }
  };

  //COPIAR TOKEN
  const handleCopy = (event, value) => {
    event.stopPropagation();
    copy(value);
    enqueueSnackbar(("Se copió el Token"), {
      preventDuplicate: true,
      variant: 'success'
    });
  };

  const handleConfigButtonClick = async (selectedUser) => {
    if (selectedUser.id && selectedUser.id !== 1 && selectedUser.token === null) {
      const res = await user.generateToken(selectedUser)
      enqueueSnackbar(("Se generó el Token correctamente"), {
        preventDuplicate: true,
        variant: 'success'
      });
      getUsers();
    } else if (selectedUser.id === 1) {
      enqueueSnackbar(("No es posible asignarle Token al usuario ROOT"), {
        preventDuplicate: true,
        variant: 'error'
      });
    } else {
      enqueueSnackbar(("El usuario ya tiene Token asignado"), {
        preventDuplicate: true,
        variant: 'error'
      });
    }
  }

  return (
      <Box>
        <Header title="USUARIOS" subtitle="Lista de Usuarios" />
        <DataTable
            loading={!users.length}
            rows={users}
            columns={[
              { field: "id", headerName: "ID", flex: 0.1 },
              { field: "nombre", headerName: "Nombre", flex: 0.7,
                renderCell: (params) => (
                    <Tooltip title={params?.value ?? ""} arrow>
                      <span className="table-cell-trucate">{params?.value ?? ""}</span>
                    </Tooltip>
                ) },
              { field: "email", headerName: "Email", flex: 0.7},
              { field: "role", headerName: "Sistema", flex: 0.7, renderCell: (params) => {
                      const roleName = params.value;
                      const label = roles.find(role => role.role === roleName)?.label ?? null;
                      return <div>{label}</div>
                  }},
              { field: "token", headerName: "Token", flex: 1.2,
                renderCell: (params) => (
                    <>
                      <IconButton
                          onClick={(event) => handleCopy(event, params?.value ?? "")}
                          aria-label="copy"
                      >
                        <ContentCopy/>
                      </IconButton>
                      <IconButton
                          onClick={() => handleConfigButtonClick(params?.row)}
                          aria-label="generate">
                        <AddIcon/>
                      </IconButton>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params?.value ?? ""}</span>
                    </>
                ),
              },
            ]}
            rowActions={[
              /*
              Se comenta para que no se pueda editar un usuario, solamente habilitamos si lo piden.
              Si permitimos que se editen los datos de usuario los tokens no van a ser validados.
              {
                label: "Editar",
                icon: <EditIcon/>,
                action: handleEdit,
              },*/
              {
                label: "Eliminar",
                icon: <DeleteIcon/>,
                action: handleAlertOpen,
                showInMenu: true,
              },
            ]}
            headerActions={[
              {
                label: "Crear usuario",
                icon: <AddBoxIcon/>,
                action: handleCreateUser,
              },
            ]}
        />

        {/* MODAL DE CREACIÓN / EDICIÓN DE USUARIO */}
        <DebFormModal
            maxWidth="sm"
            fullWidth={true}
            open={modalState}
            onClose={closeModal}
            onReject={closeModal}
            initialValues={modalInitialValues}
            onSubmit={handleSubmit}
            formikProps={{ enableReinitialize: true , validationSchema: modalInitialValues?.id ? editUserValidationSchema: newUserValidationSchema}}
            headerText={
              modalInitialValues?.id ? "Editar Usuario" : "Crear Usuario"
            }>
          <Stack spacing={1}>
            <DebFormTextInput label={"Nombre"} name={"nombre"} />
            <DebFormTextInput label={"Email"} name={"email"} />
            <DebFormPasswordInput label={"Contraseña"} name={"password"} helperText={modalInitialValues?.id ? "Dejar vacío para no modificar":""} />
            <DebFormPasswordInput label={"Reingrese contraseña"} name={"doublePassword"} helperText={modalInitialValues?.id ? "Dejar vacío para no modificar":""} />
            <DebFormSelect
                label={"Sistema"}
                name={"role"}
                //Sin Citas y Mobile por el momento, solo roles de Survey
                selectOptions={ roles.filter(role=> role.role.includes("ROLE_SURVEY")) }
            />
          </Stack>
        </DebFormModal>

        {/* ALERT DE ELIMINACIÓN */}
        <Dialog
            open={openAlert}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleAlertClose}
            aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle sx={{m:"2rem 3rem 1rem 3rem", gap:"1rem", p:"0"}} >{"¿Esta seguro que desea eliminar?"}</DialogTitle>
          <DialogActions>
            <Button onClick={handleAlertClose} variant="outlined" color="error">Cancelar</Button>
            <Button onClick={handleDelete} variant="contained" color="success">Confirmar</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}

export default Users;
